import {css, useMediaQuery, useTheme, Typography} from '@mui/material';
import {
  ConfigObjectType,
  GetStylesFunction,
  IconHeaderWithSearch,
  mixins,
  omitNullsy,
  LightSplitLayout,
  useLocationQuery,
  useXlOrLarger,
  enumContainer,
  LoadingSpinner,
  ArrayUtils,
  ExportUtils,
  useCurrentLocale,
  ConfirmDialog,
  DlMuiDynamicIcon,
  HierarchyNode,
  useApiQuery,
} from '@wallaby-ui/core';
import {
  JustTooltipFormatter,
  HaidiEncounterLinkFormatter,
  HaidiHaiTypeFormatter,
  HaidiHaiStateFormatter,
  AG_GRID_COMPATIBILITY,
  useTableData,
  MaterialTableWrapper,
  getDefaultMTOptions,
  DEFAULT_PAGE_SIZE,
  useFormatters,
  Formatters,
  HaidiSchema,
  LightSelectedFilters,
  LightSelectedFiltersPane,
  LightTextFilter,
  LightDateRangeFilter,
  LightSelectFilter,
  useAgens,
  useTableParams,
  clampPageNumber,
  useOrganizations,
  useUserFilters,
  UserFilterDto,
  LightFilterPanel,
  TableSorting,
  TablePaging,
  LightOrganizationFilter2,
  useLightOrganizationOptions,
  ShortcutEnum,
  NormalizedOption,
  LightHierarchyFilter,
  HaidiHaiTypeIconFormattedChip,
  getHaiTypeColor,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useCallback, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';

import {selectValueToValueList, valueListToSelectValue, withSelectedValues, withUnassignedOption} from './valueListFilterUtils';
import {useInvasiveDevices} from '../encounter/hai/useInvasiveDevices';
import {CodeToTranslationsMap, useStandardTypes} from '../encounter/hai/useStandardTypes';
import {useValueListField} from '../encounter/hai/useValueListField';
import {useAppSelector} from 'src/js/store_hooks';

function useHaisLayoutSplitConfig() {
  let sizes = [175, 508];
  const smallerThan1280 = useMediaQuery('(max-width:1280px)', {noSsr: true});
  const smallerThan1440 = useMediaQuery('(max-width:1440px)', {noSsr: true});
  if (smallerThan1280) {
    sizes = [283, 400];
  } else if (smallerThan1440) {
    sizes = [322, 361];
  }

  return [
    {
      sizes,
      minSize: [64, 100],
      layoutId: '/SplitLayout.HaisList.A',
    },
  ];
}

interface OwnProps {
  config: ConfigObjectType;
  route: string;
}

type Props = OwnProps;

type RowType = HaidiSchema.components['schemas']['HaiDiscoverySimpleDto'];
function useColumns(formatters: Formatters, defaultSort: TableSorting, standardTypeTranslations: CodeToTranslationsMap): Column<RowType>[] {
  const {t, formatDate} = formatters;
  const xlOrLarger = useXlOrLarger();
  const columns: Column<RowType>[] = [
    {
      field: 'encounter',
      title: t('hai.list.grid.encounter'),
      tooltip: t('hai.list.grid.encounter.tooltip'),
      render: (data) => {
        const dates =
          data.encounterType === 'HOSPITALIZATION'
            ? [
                formatDate(data?.encounterStartDate),
                data?.encounterEndDate ? formatDate(data?.encounterEndDate) : t('case.encounter.inProgress'),
              ]
            : [formatDate(data?.encounterStartDate)];
        const formattedText = [data?.patientFullName, data?.encounterOrganizationShortName, dates.join(' \u2013 ')].join(', ');
        return <HaidiEncounterLinkFormatter {...AG_GRID_COMPATIBILITY} value={formattedText} data={data} />;
      },
      width: xlOrLarger ? '' : '300px',
      sorting: true,
    },
    {
      field: 'patientIdentificationNumber',
      title: t('hai.list.grid.patientIdentificationNumber'),
      tooltip: t('hai.list.grid.patientIdentificationNumber.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data?.patientIdentificationNumber} />,
      width: xlOrLarger ? '190px' : '',
    },
    {
      field: 'organizationShortName',
      title: t('hai.list.grid.department'),
      tooltip: t('hai.list.grid.department.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data?.organizationShortName}
          tooltipOverride={data?.organizationLongLabel}
        />
      ),
      width: xlOrLarger ? '164px' : '',
      sorting: true,
    },
    {
      field: 'date',
      title: t('hai.list.grid.date'),
      tooltip: t('hai.list.grid.date.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={formatDate(data?.haiDate)} />,
      width: xlOrLarger ? '187px' : '',
      sorting: true,
    },
    {
      field: 'type',
      title: t('hai.list.grid.haiType'),
      tooltip: t('hai.list.grid.haiType.tooltip'),
      render: (data) => {
        const dataWithTranslation = {
          ...data,
          standardTypeTranslation: data.standardTypeCodeLabel ? standardTypeTranslations[data.standardTypeCodeLabel] : undefined,
        };
        return <HaidiHaiTypeFormatter {...AG_GRID_COMPATIBILITY} value={data?.type} data={dataWithTranslation} />;
      },
      width: xlOrLarger ? '242px' : '',
      sorting: true,
    },
    {
      field: 'state',
      title: t('hai.list.grid.haiState'),
      tooltip: t('hai.list.grid.haiState.tooltip'),
      render: (data) => <HaidiHaiStateFormatter {...AG_GRID_COMPATIBILITY} value={data?.state} data={data} />,
      width: xlOrLarger ? '211px' : '',
      sorting: true,
    },
    {
      field: 'comment',
      title: t('hai.list.grid.comment'),
      tooltip: t('hai.list.grid.comment.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data?.comment || '-'}
          tooltipOverride={data?.comment ? data?.comment : ''}
        />
      ),
      width: xlOrLarger ? '165px' : '',
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort: () => 0,
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

type HaiListFilters = {
  patientFullName: string;
  patientIdentificationNumber: string;
  encounterTypes: string[];
  detectionDate: {startDate: string; endDate: string; floatingShortcutName: ShortcutEnum | null} | undefined | null;
  types: string[];
  bsiOrigins: string[];
  states: string[];
  organizationIds: string[];
  onsetOrganizationIds: string[];
  onsetOrganizationsIncludeEmpty: boolean | undefined;
  checkedByEpi: boolean[];
  origins: string[];
  pathogenIds: string[];
  death: string[];
  reporters: string[];
  definitions: string[];
  comment: string;
  patientSrcId: string;
  standardTypes: string[];
  standardTypesIncludeEmpty: boolean | undefined;
  invasiveDevices: string[];
  invasiveDevicesIncludeEmpty: boolean | undefined;
  invasiveDeviceDateFrom: string;
  invasiveDeviceDateTo: string;
  invasiveDeviceDateShortcutName: ShortcutEnum | null;
  surgeryTypes: string[];
  surgeryTypesIncludeEmpty: boolean | undefined;
  surgeryDateFrom: string;
  surgeryDateTo: string;
  surgeryDateShortcutName: ShortcutEnum | null;
  userTags: string[];
  userTagsIncludeEmpty: boolean | undefined;
};
export type HaiListLinkFilters = Partial<HaiListFilters>;
type FiltersData = {
  haisOpen: boolean;
  hais: HaiListFilters;
  haisSorting: TableSorting;
  haisPaging: TablePaging;
};
const DEFAULT_HAIS_FILTERS_OPEN = true;
const DEFAULT_HAIS_FILTERS = {};
const DEFAULT_HAIS_SORTING = {};
const DEFAULT_HAIS_PAGING = {page: 0, pageSize: 10};

const HaiListLayout: React.FC<Props> = (props) => {
  const {config, route: _} = props;
  const component = 'haiList';
  const storageIdPrefix = '/hais';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(() => {
    return {
      haisOpen: query?.haisOpen ?? DEFAULT_HAIS_FILTERS_OPEN,
      hais: query?.hais ?? DEFAULT_HAIS_FILTERS,
      haisSorting: query?.haisSorting ?? DEFAULT_HAIS_SORTING,
      haisPaging: query?.haisPaging ?? DEFAULT_HAIS_PAGING,
    };
  }, [query]);
  // Loaded before user filters so the standard type id<->code mapping is available when an
  // applied (e.g. default) saved filter has to be denormalized back to codes.
  const standardTypesHook = useStandardTypes({config, selectedType: undefined});
  if (standardTypesHook.isLoading) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <HaiListLayoutWithUserFilters
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      filters={filters}
      changeFilters={changeFilters}
      standardTypesHook={standardTypesHook}
    />
  );
};
HaiListLayout.displayName = 'HaiListLayout';

type WithUserFiltersProps = Props & {
  storageIdPrefix: string;
  component: string;
  filters: FiltersData;
  changeFilters: (filterDiff: Record<string, any>) => void;
  standardTypesHook: ReturnType<typeof useStandardTypes>;
};
const HaiListLayoutWithUserFilters: React.FC<WithUserFiltersProps> = (props) => {
  const {config, storageIdPrefix, component, changeFilters, standardTypesHook} = props;
  const idToCode = useMemo(
    () => new Map<number, string>(standardTypesHook.standardTypes.map((type) => [type.id, type.code])),
    [standardTypesHook.standardTypes]
  );
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({hais: denormalizeStandardTypes(newFilters, idToCode)}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <HaiListLayoutInner
      {...props}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      idToCode={idToCode}
    />
  );
};
HaiListLayoutWithUserFilters.displayName = 'HaiListLayoutWithUserFilters';
function normalizeHaiListFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.detectionDate?.floatingShortcutName) {
    delete (copy.detectionDate as any).startDate;
    delete (copy.detectionDate as any).endDate;
    if (Object.keys(copy.detectionDate as any).length === 0) {
      delete (copy as any).detectionDate;
    }
  }
  if (copy.invasiveDeviceDateShortcutName) {
    delete (copy as any).invasiveDeviceDateFrom;
    delete (copy as any).invasiveDeviceDateTo;
  }
  if (copy.surgeryDateShortcutName) {
    delete (copy as any).surgeryDateFrom;
    delete (copy as any).surgeryDateTo;
  }
  return copy;
}

/* An empty array is not "no filter" for the request builder: omitNullsy only drops null/undefined, so a
   leftover [] (e.g. from an older stored filter) would be serialized as a bare `<field>=` query parameter. */
function nonEmpty<T>(values: T[] | undefined): T[] | undefined {
  return values?.length ? values : undefined;
}

// The ECDC type Select works with standard type codes, but the backend HaiDiscoveryFilterDto
// expects standard type ids. Saved filters are validated against that DTO, so codes are mapped
// to ids before storing and back to codes when a saved filter is applied.
function normalizeStandardTypes(
  filter: Record<string, any>,
  getStandardTypeIdForCode: (code: string) => number | undefined
): Record<string, any> {
  const codes = filter?.standardTypes as string[] | undefined;
  if (!codes?.length) return filter;
  const ids = codes.map(getStandardTypeIdForCode).filter((id): id is number => id != null);
  return {...filter, standardTypes: ids.length ? ids : undefined};
}

function denormalizeStandardTypes(filter: Record<string, any>, idToCode: Map<number, string>): Record<string, any> {
  const ids = filter?.standardTypes as Array<number | string> | undefined;
  if (!ids?.length) return filter;
  const codes = ids.map((v) => (typeof v === 'number' ? idToCode.get(v) : v)).filter((c): c is string => c != null);
  return {...filter, standardTypes: codes.length ? codes : undefined};
}
type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Record<string, any>) => void;
  standardTypesHook: ReturnType<typeof useStandardTypes>;
  idToCode: Map<number, string>;
};
const HaiListLayoutInner: React.FC<InnerProps> = (props) => {
  const {
    config,
    route: _,
    storageIdPrefix,
    component,
    userFiltersLoading,
    addedUserFilters,
    userFilters,
    changeUserFilter,
    filters,
    changeFilters,
    standardTypesHook,
    idToCode,
  } = props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  // get data
  const haiListUrl = config.service.url.haiList;
  const {
    standardType,
    standardTypeOptionsUnsorted,
    isLoading: standardTypesLoading,
    getHaiTypeMappingForCode,
    standardTypeTranslations,
    getStandardTypeIdForCode,
  } = standardTypesHook;
  const standardTypeIds = useMemo(() => {
    if (!getStandardTypeIdForCode || !filters.hais.standardTypes) return undefined;
    const ids = filters.hais.standardTypes.map(getStandardTypeIdForCode).filter((id): id is number => id != null);
    return ids.length ? ids : undefined;
  }, [filters.hais.standardTypes, getStandardTypeIdForCode]);
  // Saved filters store standard types as ids (to match HaiDiscoveryFilterDto); the UI keeps codes.
  const savedFilterValue = useMemo(
    () => normalizeStandardTypes(filters.hais, getStandardTypeIdForCode),
    [filters.hais, getStandardTypeIdForCode]
  );
  const haiListParams = useTableParams(
    {
      page: filters.haisPaging.page ?? 0,
      size: filters.haisPaging.pageSize ?? 0,
      patientFullName: filters.hais.patientFullName,
      patientIdentificationNumber: filters.hais.patientIdentificationNumber,
      encounterTypes: filters.hais.encounterTypes,
      dateFrom: filters.hais.detectionDate?.startDate,
      dateTo: filters.hais.detectionDate?.endDate,
      floatingShortcutName: filters.hais.detectionDate?.floatingShortcutName,
      types: filters.hais.types,
      bsiOrigins: filters.hais.bsiOrigins,
      states: filters.hais.states,
      organizationIds: filters.hais.organizationIds,
      onsetOrganizationIds: filters.hais.onsetOrganizationIds,
      // valid only when true — undefined/false is never sent
      onsetOrganizationsIncludeEmpty: filters.hais.onsetOrganizationsIncludeEmpty || undefined,
      checkedByEpi: filters.hais.checkedByEpi,
      origins: filters.hais.origins,
      pathogenIds: filters.hais.pathogenIds,
      death: filters.hais.death,
      reporters: filters.hais.reporters,
      definitions: filters.hais.definitions,
      comment: filters.hais.comment,
      patientSrcId: filters.hais.patientSrcId,
      standardTypes: standardTypeIds,
      standardTypesIncludeEmpty: filters.hais.standardTypesIncludeEmpty,
      invasiveDevices: nonEmpty(filters.hais.invasiveDevices),
      invasiveDevicesIncludeEmpty: filters.hais.invasiveDevicesIncludeEmpty,
      invasiveDeviceDateFrom: filters.hais.invasiveDeviceDateFrom,
      invasiveDeviceDateTo: filters.hais.invasiveDeviceDateTo,
      invasiveDeviceDateShortcutName: filters.hais.invasiveDeviceDateShortcutName,
      surgeryTypes: nonEmpty(filters.hais.surgeryTypes),
      surgeryTypesIncludeEmpty: filters.hais.surgeryTypesIncludeEmpty,
      surgeryDateFrom: filters.hais.surgeryDateFrom,
      surgeryDateTo: filters.hais.surgeryDateTo,
      surgeryDateShortcutName: filters.hais.surgeryDateShortcutName,
      userTags: nonEmpty(filters.hais.userTags),
      userTagsIncludeEmpty: filters.hais.userTagsIncludeEmpty,
    },
    filters.haisSorting,
    {
      encounter: ['patient.lastName', 'patient.firstName', 'encounter.organization.shortName', 'encounter.startedAt'],
      organizationShortName: 'organization.shortName',
    }
  );
  const {data, isLoading, page, countOnPage, totalCount, totalPages} = useTableData<any, RowType[]>(haiListUrl, haiListParams);

  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  const hasFilters = !isEqual(omitNullsy(filters.hais), omitNullsy(DEFAULT_HAIS_FILTERS));
  const hasSorting = !isEqual(omitNullsy(filters.haisSorting), omitNullsy(DEFAULT_HAIS_SORTING));

  const columns = useColumns(formatters, filters.haisSorting, standardTypeTranslations);
  const enumMap = useAppSelector(
    (state) => (state[enumContainer.ENUM_REDUCER_STATE_ID] as any).enumMap as Record<string, enumContainer.EnumDefinition[]>
  );
  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const agensData = useAgens(config);
  const sortedAgensData = useMemo(() => sortBy(agensData ?? [], (v) => v.label), [agensData]);
  const {data: organizations} = useOrganizations(config);
  // IDEA-290: the onset-department filter carries a leading "-" option that maps to the
  // onsetOrganizationsIncludeEmpty flag (HAIs whose onset department is unknown). A dash rather
  // than an "Unknown" label because a connector can deliver an organization actually named
  // "Unknown"; the HAI form itself still displays null as Unknown.
  const {options: onsetOrganizationOptionsBase} = useLightOrganizationOptions({appConfig: config});
  const onsetOrganizationOptions = useMemo(
    () => withUnassignedOption(onsetOrganizationOptionsBase as unknown as NormalizedOption<string>[]),
    [onsetOrganizationOptionsBase]
  );
  const lightSelectFilterMaxWidth = 215;
  const haisLayoutSplitConfig = useHaisLayoutSplitConfig();
  // Bottom-most filter-inputs row; measured by autoFitFilterRows to default the divider to 2 rows + a peek.
  const filterInputsRef = useRef<HTMLDivElement>(null);
  const currentLocale = useCurrentLocale();

  const apiUrl = config.service.url.maxExportRows;

  const {data: maxExportedRows} = useApiQuery<number | undefined>([apiUrl], null, {
    axiosConfig: {
      url: apiUrl,
    },
    reactQueryOptions: {
      enabled: true,
    },
  });

  const downloadXlsxExport = useCallback(() => {
    ExportUtils.handleExportXlsx({
      url: config.service.url.haiListXlsxExport,
      filters: {
        patientSrcId: filters.hais.patientSrcId,
        patientFullName: filters.hais.patientFullName,
        patientIdentificationNumber: filters.hais.patientIdentificationNumber,
        encounterTypes: filters.hais.encounterTypes,
        dateFrom: filters.hais.detectionDate?.startDate,
        dateTo: filters.hais.detectionDate?.endDate,
        types: filters.hais.types,
        bsiOrigins: filters.hais.bsiOrigins,
        states: filters.hais.states,
        organizationIds: filters.hais.organizationIds,
        onsetOrganizationIds: filters.hais.onsetOrganizationIds,
        onsetOrganizationsIncludeEmpty: filters.hais.onsetOrganizationsIncludeEmpty || undefined,
        checkedByEpi: filters.hais.checkedByEpi,
        origins: filters.hais.origins,
        pathogenIds: filters.hais.pathogenIds,
        death: filters.hais.death,
        reporters: filters.hais.reporters,
        definitions: filters.hais.definitions,
        comment: filters.hais.comment,
        standardTypes: standardTypeIds,
        standardTypesIncludeEmpty: filters.hais.standardTypesIncludeEmpty,
        invasiveDevices: nonEmpty(filters.hais.invasiveDevices),
        invasiveDevicesIncludeEmpty: filters.hais.invasiveDevicesIncludeEmpty,
        invasiveDeviceDateFrom: filters.hais.invasiveDeviceDateFrom,
        invasiveDeviceDateTo: filters.hais.invasiveDeviceDateTo,
        invasiveDeviceDateShortcutName: filters.hais.invasiveDeviceDateShortcutName,
        surgeryTypes: nonEmpty(filters.hais.surgeryTypes),
        surgeryTypesIncludeEmpty: filters.hais.surgeryTypesIncludeEmpty,
        surgeryDateFrom: filters.hais.surgeryDateFrom,
        surgeryDateTo: filters.hais.surgeryDateTo,
        surgeryDateShortcutName: filters.hais.surgeryDateShortcutName,
        userTags: nonEmpty(filters.hais.userTags),
        userTagsIncludeEmpty: filters.hais.userTagsIncludeEmpty,
      },
      locale: currentLocale,
    });
  }, [config.service.url.haiListXlsxExport, currentLocale, filters.hais, standardTypeIds]);
  const [confirmExportWarningOpened, setConfirmExportWarningOpened] = useState(false);
  const onConfirmExportWarning = useCallback(() => {
    setConfirmExportWarningOpened(false);
    downloadXlsxExport();
  }, [downloadXlsxExport]);
  const handleDialogCancel = () => {
    // do nothing
  };

  const {invasiveDeviceOptions, getInvasiveDeviceLabel} = useInvasiveDevices({config});
  const invasiveDeviceOptionsWithUnassigned = useMemo(
    () => withUnassignedOption(withSelectedValues(invasiveDeviceOptions, filters.hais.invasiveDevices, getInvasiveDeviceLabel)),
    [invasiveDeviceOptions, filters.hais.invasiveDevices, getInvasiveDeviceLabel]
  );

  const getInvasiveDevicesValue = useCallback(
    () => valueListToSelectValue(filters.hais.invasiveDevices, filters.hais.invasiveDevicesIncludeEmpty),
    [filters.hais.invasiveDevices, filters.hais.invasiveDevicesIncludeEmpty]
  );

  const handleInvasiveDevicesChange = useCallback(
    (newValue: string[]) => {
      const {values, includeEmpty} = selectValueToValueList(newValue);
      changeFilters({
        hais: {
          ...filters.hais,
          invasiveDevices: values,
          invasiveDevicesIncludeEmpty: includeEmpty,
        },
        haisPaging: {
          ...filters.haisPaging,
          page: 0,
        },
      });
    },
    [filters.hais, filters.haisPaging, changeFilters]
  );

  const {options: surgeryTypeOptions} = useValueListField({config, url: config.service.url.surgeryTypeValues});
  const surgeryTypeOptionsWithUnassigned = useMemo(
    () => withUnassignedOption(withSelectedValues(surgeryTypeOptions, filters.hais.surgeryTypes)),
    [surgeryTypeOptions, filters.hais.surgeryTypes]
  );

  const getSurgeryTypesValue = useCallback(
    () => valueListToSelectValue(filters.hais.surgeryTypes, filters.hais.surgeryTypesIncludeEmpty),
    [filters.hais.surgeryTypes, filters.hais.surgeryTypesIncludeEmpty]
  );

  const handleSurgeryTypesChange = useCallback(
    (newValue: string[]) => {
      const {values, includeEmpty} = selectValueToValueList(newValue);
      changeFilters({
        hais: {
          ...filters.hais,
          surgeryTypes: values,
          surgeryTypesIncludeEmpty: includeEmpty,
        },
        haisPaging: {
          ...filters.haisPaging,
          page: 0,
        },
      });
    },
    [filters.hais, filters.haisPaging, changeFilters]
  );

  const {options: userTagOptions} = useValueListField({config, url: config.service.url.userTagValues});
  const userTagOptionsWithUnassigned = useMemo(
    () => withUnassignedOption(withSelectedValues(userTagOptions, filters.hais.userTags)),
    [userTagOptions, filters.hais.userTags]
  );

  const getUserTagsValue = useCallback(
    () => valueListToSelectValue(filters.hais.userTags, filters.hais.userTagsIncludeEmpty),
    [filters.hais.userTags, filters.hais.userTagsIncludeEmpty]
  );

  const handleUserTagsChange = useCallback(
    (newValue: string[]) => {
      const {values, includeEmpty} = selectValueToValueList(newValue);
      changeFilters({
        hais: {
          ...filters.hais,
          userTags: values,
          userTagsIncludeEmpty: includeEmpty,
        },
        haisPaging: {
          ...filters.haisPaging,
          page: 0,
        },
      });
    },
    [filters.hais, filters.haisPaging, changeFilters]
  );

  const UNASSIGNED_STANDARD_TYPE_ID = '__UNASSIGNED__';
  const standardTypeOptionsWithUnassigned = useMemo(() => {
    const unassignedOption: NormalizedOption = {
      id: UNASSIGNED_STANDARD_TYPE_ID,
      label: t('reports.uncategorized'),
      original: null,
    };
    return [unassignedOption, ...standardTypeOptionsUnsorted];
  }, [standardTypeOptionsUnsorted, t]);

  const getStandardTypesValue = useCallback(() => {
    const values = [...(filters.hais.standardTypes ?? [])];
    if (filters.hais.standardTypesIncludeEmpty) {
      values.push(UNASSIGNED_STANDARD_TYPE_ID);
    }
    return values;
  }, [filters.hais.standardTypes, filters.hais.standardTypesIncludeEmpty]);

  const getHaiTypeTagCss = useCallback((haiType) => {
    let bgColor: string = 'unset';
    if (haiType != null) {
      const haiTypeColor = getHaiTypeColor({type: haiType});
      if (haiTypeColor.background != null) {
        bgColor = haiTypeColor.background;
      }
    }
    return css`
      background: ${bgColor};
    `;
  }, []);

  const getStandardTypeTagCss = useCallback(
    (standardTypeCode) => {
      return getHaiTypeTagCss(getHaiTypeMappingForCode(standardTypeCode));
    },
    [getHaiTypeMappingForCode, getHaiTypeTagCss]
  );

  const handleStandardTypesChange = useCallback(
    (newValue: string[]) => {
      const includesUnassigned = (newValue ?? []).includes(UNASSIGNED_STANDARD_TYPE_ID);
      const filteredValues = (newValue ?? []).filter((v) => v !== UNASSIGNED_STANDARD_TYPE_ID);

      changeFilters({
        hais: {
          ...filters.hais,
          standardTypes: filteredValues,
          standardTypesIncludeEmpty: includesUnassigned ? true : undefined,
        },
        haisPaging: {
          ...filters.haisPaging,
          page: 0,
        },
      });
    },
    [filters.hais, filters.haisPaging, changeFilters]
  );

  const getNodeIcon = useCallback(
    (node: HierarchyNode<NormalizedOption>) => {
      return standardTypesLoading ? null : (
        <span
          style={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: `${
              node?.original?.original?.haiTypeMapping
                ? getHaiTypeColor({type: node.original.original.haiTypeMapping, state: ''}).background
                : 'lightgray'
            }`,
          }}
        />
      );
    },
    [standardTypesLoading]
  );
  const bsiOriginOptions = useMemo(
    () =>
      sortBy(
        enumMap.BSIOrigin.filter((v) => !v.isDeprecated).map((v) => ({id: v.name, label: `${t(v.messageKey)}`})),
        [(v) => v.label]
      ),
    [enumMap.BSIOrigin, t]
  );
  const tableFiltersForSelectedPane = useMemo(() => {
    const invasiveDevicesValue = getInvasiveDevicesValue();
    const hasInvasiveDevices = invasiveDevicesValue.length > 0;

    const surgeryTypesValue = getSurgeryTypesValue();
    const hasSurgeryTypes = surgeryTypesValue.length > 0;

    const userTagsValue = getUserTagsValue();
    const hasUserTags = userTagsValue.length > 0;

    const hasSurgeryDate = !!filters.hais.surgeryDateFrom && !!filters.hais.surgeryDateTo;

    const hasInvasiveDeviceDate = !!filters.hais.invasiveDeviceDateFrom && !!filters.hais.invasiveDeviceDateTo;

    const hasDetectionDate = !!filters.hais.detectionDate?.startDate && !!filters.hais.detectionDate.endDate;

    const hasStandardTypes = (filters.hais.standardTypes?.length ?? 0) > 0 || !!filters.hais.standardTypesIncludeEmpty;

    const hasOnsetOrganizations =
      (filters.hais.onsetOrganizationIds?.length ?? 0) > 0 || !!filters.hais.onsetOrganizationsIncludeEmpty;

    const result: any = {
      ...filters.hais,
    };

    if (!hasStandardTypes) {
      delete result.standardTypes;
      delete result.standardTypesIncludeEmpty;
    }

    if (!hasOnsetOrganizations) {
      delete result.onsetOrganizationIds;
      delete result.onsetOrganizationsIncludeEmpty;
    }

    if (hasInvasiveDevices) {
      result.invasiveDevices = invasiveDevicesValue;
    } else {
      delete result.invasiveDevices;
      delete result.invasiveDevicesIncludeEmpty;
    }

    if (hasDetectionDate) {
      result.detectionDate = {
        startDate: filters.hais.detectionDate?.startDate,
        endDate: filters.hais.detectionDate?.endDate,
        shortcut: filters.hais.detectionDate?.floatingShortcutName,
      };
    } else {
      delete result.detectionDate;
    }

    if (hasInvasiveDeviceDate) {
      result.invasiveDeviceDate = {
        startDate: filters.hais.invasiveDeviceDateFrom,
        endDate: filters.hais.invasiveDeviceDateTo,
        shortcut: filters.hais.invasiveDeviceDateShortcutName,
      };
    } else {
      delete result.invasiveDeviceDate;
    }

    if (hasSurgeryTypes) {
      result.surgeryTypes = surgeryTypesValue;
    } else {
      delete result.surgeryTypes;
      delete result.surgeryTypesIncludeEmpty;
    }

    if (hasSurgeryDate) {
      result.surgeryDate = {
        startDate: filters.hais.surgeryDateFrom,
        endDate: filters.hais.surgeryDateTo,
        shortcut: filters.hais.surgeryDateShortcutName,
      };
    } else {
      delete result.surgeryDate;
    }

    if (hasUserTags) {
      result.userTags = userTagsValue;
    } else {
      delete result.userTags;
      delete result.userTagsIncludeEmpty;
    }

    return result;
  }, [filters.hais, getInvasiveDevicesValue, getSurgeryTypesValue, getUserTagsValue]);
  const haiTypesWithoutUnspecified = useMemo(
    () =>
      sortBy(
        enumMap.HaiTypeEnum.filter((v) => !v.isDeprecated).map((v) => ({
          id: v.name,
          label:
            v.name === 'BSI'
              ? t(v.messageKey)
                  .replace(/\s*\([^)]*\)/g, '')
                  .trim()
              : t(v.messageKey),
          original: {
            code: v.name,
          },
        })),
        [(v) => v.label]
      ),
    [enumMap.HaiTypeEnum, t]
  );

  const selectedFiltersPane =
    Object.keys(omitNullsy(tableFiltersForSelectedPane)).length > 0 ? (
      <LightSelectedFiltersPane
        selectedFilters={[
          {
            name: 'patientFullName',
            label: t('filter.hai.patientFullName'),
            value: filters.hais.patientFullName,
          },
          {
            name: 'patientIdentificationNumber',
            label: t('filter.hai.patientIdentificationNumber'),
            value: filters.hais.patientIdentificationNumber,
          },
          {
            name: 'encounterTypes',
            label: t('filter.hai.encounterType'),
            value: (filters.hais.encounterTypes ?? []).map((v) => tEnum('EncounterTypeEnum', v)).join(', '),
          },
          {
            name: 'detectionDate',
            label: t('filter.hai.date'),
            value: `${formatDate(filters.hais.detectionDate?.startDate ?? '')} - ${formatDate(filters.hais.detectionDate?.endDate ?? '')}`,
          },
          {
            name: 'types',
            label: t('filter.hai.type'),
            value: (filters.hais.types ?? []).map((v) => tEnum('HaiTypeEnum', v)).join(', '),
          },
          {
            name: 'bsiOrigins',
            label: t('filter.hai.bsiOrigin'),
            value: (filters.hais.bsiOrigins ?? []).map((v) => tEnum('BSIOrigin', v)).join(', '),
          },
          {
            name: 'states',
            label: t('filter.hai.state'),
            value: (filters.hais.states ?? []).map((v) => tEnum('State', v)).join(', '),
          },
          {
            name: 'organizationIds',
            label: t('filter.hai.department'),
            value: (filters.hais.organizationIds ?? [])
              .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName)
              .join(', '),
          },
          {
            name: 'onsetOrganizationIds',
            label: t('filter.hai.onsetDepartment'),
            value: [
              ...(filters.hais.onsetOrganizationIds ?? []).map(
                (id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName
              ),
              ...(filters.hais.onsetOrganizationsIncludeEmpty ? ['-'] : []),
            ].join(', '),
          },
          {
            name: 'checkedByEpi',
            label: t('filter.hai.checkedByEpi'),
            value: (ArrayUtils.mapBooleanToEnum(filters.hais.checkedByEpi, enumMap.CheckedByEpi, (v) => tEnum('CheckedByEpi', v)) ?? [])
              .map((v) => tEnum('CheckedByEpi', v))
              .join(', '),
          },
          {
            name: 'origins',
            label: t('filter.hai.origin'),
            value: (filters.hais.origins ?? []).map((v) => tEnum('Origin', v)).join(', '),
          },
          {
            name: 'pathogenIds',
            label: t('filter.hai.pathogen'),
            value: (filters.hais.pathogenIds ?? []).map((id) => agensData.find((w) => String(w.id) === String(id))?.label).join(', '),
          },
          {
            name: 'death',
            label: t('filter.hai.death'),
            value: (filters.hais.death ?? []).map((v) => tEnum('HaiDeathEnum', v)).join(', '),
          },
          {
            name: 'reporters',
            label: t('filter.hai.reportedBy'),
            value: (filters.hais.reporters ?? []).map((v) => tEnum('Reporter', v)).join(', '),
          },
          {
            name: 'definitions',
            label: t('filter.hai.definition'),
            value: (filters.hais.definitions ?? []).map((v) => tEnum('Definition', v)).join(', '),
          },
          {
            name: 'comment',
            label: t('filter.hai.comment'),
            value: filters.hais.comment,
          },
          {
            name: 'patientSrcId',
            label: t('filter.hai.patientSrcId'),
            value: filters.hais.patientSrcId,
          },
          {
            name: 'standardTypes',
            label: t(`hai.miniScreen.hai.standardType.${standardType}`),
            value: [
              ...(filters.hais.standardTypes ?? []).map(
                (code) => standardTypeOptionsUnsorted.find((opt) => opt.id === code)?.label ?? code
              ),
              ...(filters.hais.standardTypesIncludeEmpty ? [t('reports.uncategorized')] : []),
            ].join(', '),
          },
          {
            name: 'invasiveDevices',
            label: t('hai.miniScreen.hai.invasiveDevices'),
            value: [
              ...(filters.hais.invasiveDevices ?? []).map(getInvasiveDeviceLabel),
              ...(filters.hais.invasiveDevicesIncludeEmpty ? [t('reports.uncategorized')] : []),
            ].join(', '),
          },
          {
            name: 'invasiveDeviceDate',
            label: t('hai.miniScreen.hai.invasiveDeviceDate'),
            value: `${formatDate(filters.hais.invasiveDeviceDateFrom ?? '')} - ${formatDate(filters.hais.invasiveDeviceDateTo ?? '')}`,
          },
          {
            name: 'surgeryTypes',
            label: t('hai.miniScreen.hai.surgeryTypes'),
            value: [
              ...(filters.hais.surgeryTypes ?? []),
              ...(filters.hais.surgeryTypesIncludeEmpty ? [t('reports.uncategorized')] : []),
            ].join(', '),
          },
          {
            name: 'surgeryDate',
            label: t('hai.miniScreen.hai.surgeryDate'),
            value: `${formatDate(filters.hais.surgeryDateFrom ?? '')} - ${formatDate(filters.hais.surgeryDateTo ?? '')}`,
          },
          {
            name: 'userTags',
            label: t('hai.miniScreen.hai.userTags'),
            value: [...(filters.hais.userTags ?? []), ...(filters.hais.userTagsIncludeEmpty ? [t('reports.uncategorized')] : [])].join(
              ', '
            ),
          },
        ]}
        tableFilters={tableFiltersForSelectedPane}
        changeTableFilters={(newTableFilters) => {
          const hadInvasiveDevicesBefore = getInvasiveDevicesValue().length > 0;
          const hadInvasiveDeviceDateBefore = !!filters.hais.invasiveDeviceDateFrom && !!filters.hais.invasiveDeviceDateTo;
          const hadSurgeryTypesBefore = getSurgeryTypesValue().length > 0;
          const hadSurgeryDateBefore = !!filters.hais.surgeryDateFrom && !!filters.hais.surgeryDateTo;
          const hadUserTagsBefore = getUserTagsValue().length > 0;

          const invasiveDevicesExistsInNew = 'invasiveDevices' in newTableFilters;
          const invasiveDeviceDateExistsInNew = 'invasiveDeviceDate' in newTableFilters;
          const surgeryTypesExistsInNew = 'surgeryTypes' in newTableFilters;
          const surgeryDateExistsInNew = 'surgeryDate' in newTableFilters;
          const userTagsExistsInNew = 'userTags' in newTableFilters;

          const {
            invasiveDevices,
            invasiveDeviceDate,
            surgeryTypes: surgeryTypesPane,
            surgeryDate,
            userTags: userTagsPane,
            ...rest
          } = newTableFilters as typeof tableFiltersForSelectedPane;

          let {standardTypesIncludeEmpty} = filters.hais;

          if ('standardTypes' in newTableFilters) {
            const stdValue = (newTableFilters as any).standardTypes;
            if (stdValue == null || (Array.isArray(stdValue) && stdValue.length === 0)) {
              standardTypesIncludeEmpty = undefined;
            }
          }

          // Removing the onset-department chip must also clear the "-" (include empty) flag —
          // otherwise it would silently keep filtering after the chip disappeared.
          let {onsetOrganizationsIncludeEmpty} = filters.hais;
          const onsetInNew = (newTableFilters as any).onsetOrganizationIds;
          if (!('onsetOrganizationIds' in newTableFilters) || onsetInNew == null || (Array.isArray(onsetInNew) && onsetInNew.length === 0)) {
            onsetOrganizationsIncludeEmpty = undefined;
          }

          let invasiveDevicesValues: string[] | undefined = filters.hais.invasiveDevices;
          let {invasiveDevicesIncludeEmpty} = filters.hais;

          if (invasiveDevicesExistsInNew) {
            if (!invasiveDevices || (Array.isArray(invasiveDevices) && invasiveDevices.length === 0)) {
              invasiveDevicesValues = undefined;
              invasiveDevicesIncludeEmpty = undefined;
            } else {
              const {values, includeEmpty} = selectValueToValueList(invasiveDevices as any);
              invasiveDevicesValues = values;
              invasiveDevicesIncludeEmpty = includeEmpty;
            }
          } else if (hadInvasiveDevicesBefore) {
            invasiveDevicesValues = undefined;
            invasiveDevicesIncludeEmpty = undefined;
          }

          let {invasiveDeviceDateFrom} = filters.hais;
          let {invasiveDeviceDateTo} = filters.hais;
          let {invasiveDeviceDateShortcutName} = filters.hais;

          if (invasiveDeviceDateExistsInNew) {
            if (!invasiveDeviceDate) {
              invasiveDeviceDateFrom = undefined as any;
              invasiveDeviceDateTo = undefined as any;
              invasiveDeviceDateShortcutName = null;
            } else {
              const hasDates = !!invasiveDeviceDate.startDate && !!invasiveDeviceDate.endDate;
              invasiveDeviceDateFrom = hasDates ? (invasiveDeviceDate.startDate as string) : (undefined as any);
              invasiveDeviceDateTo = hasDates ? (invasiveDeviceDate.endDate as string) : (undefined as any);
              invasiveDeviceDateShortcutName = hasDates ? ((invasiveDeviceDate.shortcut ?? null) as ShortcutEnum | null) : null;
            }
          } else if (hadInvasiveDeviceDateBefore) {
            invasiveDeviceDateFrom = undefined as any;
            invasiveDeviceDateTo = undefined as any;
            invasiveDeviceDateShortcutName = null;
          }

          let surgeryTypesValues: string[] | undefined = filters.hais.surgeryTypes;
          let {surgeryTypesIncludeEmpty} = filters.hais;

          if (surgeryTypesExistsInNew) {
            if (!surgeryTypesPane || (Array.isArray(surgeryTypesPane) && surgeryTypesPane.length === 0)) {
              surgeryTypesValues = undefined;
              surgeryTypesIncludeEmpty = undefined;
            } else {
              const {values, includeEmpty} = selectValueToValueList(surgeryTypesPane as any);
              surgeryTypesValues = values;
              surgeryTypesIncludeEmpty = includeEmpty;
            }
          } else if (hadSurgeryTypesBefore) {
            surgeryTypesValues = undefined;
            surgeryTypesIncludeEmpty = undefined;
          }

          let {surgeryDateFrom} = filters.hais;
          let {surgeryDateTo} = filters.hais;
          let {surgeryDateShortcutName} = filters.hais;

          if (surgeryDateExistsInNew) {
            if (!surgeryDate) {
              surgeryDateFrom = undefined as any;
              surgeryDateTo = undefined as any;
              surgeryDateShortcutName = null;
            } else {
              const hasDates = !!surgeryDate.startDate && !!surgeryDate.endDate;
              surgeryDateFrom = hasDates ? (surgeryDate.startDate as string) : (undefined as any);
              surgeryDateTo = hasDates ? (surgeryDate.endDate as string) : (undefined as any);
              surgeryDateShortcutName = hasDates ? ((surgeryDate.shortcut ?? null) as ShortcutEnum | null) : null;
            }
          } else if (hadSurgeryDateBefore) {
            surgeryDateFrom = undefined as any;
            surgeryDateTo = undefined as any;
            surgeryDateShortcutName = null;
          }

          let userTagsValues: string[] | undefined = filters.hais.userTags;
          let {userTagsIncludeEmpty} = filters.hais;

          if (userTagsExistsInNew) {
            if (!userTagsPane || (Array.isArray(userTagsPane) && userTagsPane.length === 0)) {
              userTagsValues = undefined;
              userTagsIncludeEmpty = undefined;
            } else {
              const {values, includeEmpty} = selectValueToValueList(userTagsPane as any);
              userTagsValues = values;
              userTagsIncludeEmpty = includeEmpty;
            }
          } else if (hadUserTagsBefore) {
            userTagsValues = undefined;
            userTagsIncludeEmpty = undefined;
          }

          changeFilters({
            hais: {
              ...(rest as HaiListFilters),
              standardTypesIncludeEmpty,
              onsetOrganizationsIncludeEmpty,
              invasiveDevices: invasiveDevicesValues,
              invasiveDevicesIncludeEmpty,
              invasiveDeviceDateFrom,
              invasiveDeviceDateTo,
              invasiveDeviceDateShortcutName,
              surgeryTypes: surgeryTypesValues,
              surgeryTypesIncludeEmpty,
              surgeryDateFrom,
              surgeryDateTo,
              surgeryDateShortcutName,
              userTags: userTagsValues,
              userTagsIncludeEmpty,
            },
            haisPaging: {
              ...filters.haisPaging,
              page: 0,
            },
          });
        }}
        formatters={formatters}
      />
    ) : null;
  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={t('hai.list.pageTitle')} />
      <LightSplitLayout
        initSizes={haisLayoutSplitConfig[0].sizes}
        minSizes={haisLayoutSplitConfig[0].minSize}
        storageId={haisLayoutSplitConfig[0].layoutId}
        isVertical
        gutterSize={18}
        noGrow={[!filters.haisOpen, false]}
        hidden={[false, false]}
        autoFitFilterRows={{contentRef: filterInputsRef, enabled: filters.haisOpen}}
      >
        <div css={muiStyles.splitContentBox}>
          <LightFilterPanel
            open={filters.haisOpen}
            toggleOpen={() =>
              changeFilters({
                haisOpen: !filters.haisOpen,
              })
            }
            hasActiveFilter={hasFilters}
            filterInputsRef={filterInputsRef}
            savedFiltersProps={{
              storageIdPrefix,
              component,
              userFiltersLoading,
              addedUserFilters,
              userFilters,
              changeUserFilter,
              filter: savedFilterValue,
              setFilter: (newFilter) => {
                changeFilters({
                  hais: denormalizeStandardTypes(newFilter, idToCode),
                });
              },
              config,
              normalizeForCompare: normalizeHaiListFiltersForCompare,
            }}
            selectedFilters={selectedFiltersPane}
          >
            <LightTextFilter
              config={{
                name: 'patientFullName',
                ui: {label: 'filter.hai.patientFullName'},
              }}
              value={filters.hais.patientFullName}
              onChange={(newValue) =>
                changeFilters({
                  hais: {
                    ...filters.hais,
                    patientFullName: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                })
              }
            />
            <LightTextFilter
              config={{
                name: 'patientIdentificationNumber',
                ui: {label: 'filter.hai.patientIdentificationNumber'},
              }}
              value={filters.hais.patientIdentificationNumber}
              onChange={(newValue) =>
                changeFilters({
                  hais: {
                    ...filters.hais,
                    patientIdentificationNumber: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                })
              }
            />
            <LightSelectFilter
              config={{
                name: `encounterTypes`,
                ui: {label: 'filter.hai.encounterType'},
              }}
              options={sortByMessageKey(enumMap.EncounterTypeEnum)}
              multi
              value={filters.hais.encounterTypes}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    encounterTypes: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightSelectFilter
              config={{
                name: `types`,
                ui: {label: 'filter.hai.type'},
              }}
              options={haiTypesWithoutUnspecified}
              multi
              value={filters.hais.types}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    types: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              placeholderText={''}
              showChipsInSingleSelect
              chipCss={(original: any) => getHaiTypeTagCss(original)}
              renderOption={(props, option) => (
                <li {...props} style={{paddingLeft: '3px'}}>
                  <HaidiHaiTypeIconFormattedChip
                    value={(option as NormalizedOption)?.id ?? option}
                    state={undefined}
                    showAsText
                    showTooltip={false}
                  />
                </li>
              )}
              maxWidth={lightSelectFilterMaxWidth}
            />
            {standardType !== 'NO_TYPES' && (
              <LightHierarchyFilter
                config={{
                  name: 'standardTypes',
                  ui: {label: `hai.miniScreen.hai.standardType.${standardType}`},
                }}
                getNodeIcon={(params) => getNodeIcon(params.node)}
                chipCss={(original: any) => getStandardTypeTagCss(original)}
                options={standardTypeOptionsWithUnassigned}
                renderOption={(props, option) => {
                  return (
                    <li {...props} style={{paddingLeft: '3px'}}>
                      <HaidiHaiTypeIconFormattedChip
                        value={getHaiTypeMappingForCode(option.id)}
                        valueTranslated={option.label}
                        state={undefined}
                        showAsText
                        showTooltip
                        useEllipsis
                      />
                    </li>
                  );
                }}
                multi
                value={getStandardTypesValue()}
                onChange={handleStandardTypesChange}
                loading={standardTypesLoading}
                defaultIsExpanded
              />
            )}
            <LightSelectFilter
              config={{
                name: `bsiOrigins`,
                ui: {label: 'filter.hai.bsiOrigin'},
              }}
              options={bsiOriginOptions}
              multi
              value={filters.hais.bsiOrigins}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    bsiOrigins: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightSelectFilter
              config={{
                name: `states`,
                ui: {label: 'filter.hai.state'},
              }}
              options={sortByMessageKey(enumMap.State)}
              multi
              value={filters.hais.states}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    states: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightOrganizationFilter2
              appConfig={config}
              config={{
                name: 'organizationIds',
                ui: {label: 'filter.hai.department'},
              }}
              multi
              value={filters.hais.organizationIds}
              onChange={(newOrganizationIds) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    organizationIds: newOrganizationIds,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightHierarchyFilter
              config={{
                name: 'onsetOrganizationIds',
                ui: {label: 'filter.hai.onsetDepartment'},
              }}
              options={onsetOrganizationOptions}
              ignoreOption={(option) => Boolean((option.original as any)?.isSoloRootNode)}
              multi
              value={valueListToSelectValue(filters.hais.onsetOrganizationIds, filters.hais.onsetOrganizationsIncludeEmpty)}
              onChange={(newValue) => {
                const {values, includeEmpty} = selectValueToValueList(newValue);
                changeFilters({
                  hais: {
                    ...filters.hais,
                    onsetOrganizationIds: values,
                    onsetOrganizationsIncludeEmpty: includeEmpty,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightDateRangeFilter
              config={{
                name: 'detectionDate',
                ui: {label: 'filter.hai.date'},
              }}
              value={
                filters.hais.detectionDate?.startDate && filters.hais.detectionDate?.endDate
                  ? {
                      startDate: filters.hais.detectionDate.startDate,
                      endDate: filters.hais.detectionDate.endDate,
                      shortcut: filters.hais.detectionDate.floatingShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    detectionDate:
                      newValue && Object.keys(newValue).length > 0
                        ? {
                            startDate: newValue?.startDate,
                            endDate: newValue?.endDate,
                            floatingShortcutName: newValue?.shortcut,
                          }
                        : undefined,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              showShortcuts
            />
            <LightSelectFilter
              config={{
                name: 'surgeryTypes',
                ui: {label: 'hai.miniScreen.hai.surgeryTypes'},
              }}
              options={surgeryTypeOptionsWithUnassigned}
              multi
              value={getSurgeryTypesValue()}
              onChange={handleSurgeryTypesChange}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightDateRangeFilter
              config={{
                name: 'surgeryDate',
                ui: {label: 'hai.miniScreen.hai.surgeryDate'},
              }}
              value={
                filters.hais.surgeryDateFrom && filters.hais.surgeryDateTo
                  ? {
                      startDate: filters.hais.surgeryDateFrom,
                      endDate: filters.hais.surgeryDateTo,
                      shortcut: filters.hais.surgeryDateShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    surgeryDateFrom: newValue?.startDate,
                    surgeryDateTo: newValue?.endDate,
                    surgeryDateShortcutName: newValue?.shortcut,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              showShortcuts
            />
            <LightSelectFilter
              config={{
                name: 'invasiveDevices',
                ui: {label: 'hai.miniScreen.hai.invasiveDevices'},
              }}
              options={invasiveDeviceOptionsWithUnassigned}
              multi
              value={getInvasiveDevicesValue()}
              onChange={handleInvasiveDevicesChange}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightDateRangeFilter
              config={{
                name: 'invasiveDeviceDate',
                ui: {label: 'hai.miniScreen.hai.invasiveDeviceDate'},
              }}
              value={
                filters.hais.invasiveDeviceDateFrom && filters.hais.invasiveDeviceDateTo
                  ? {
                      startDate: filters.hais.invasiveDeviceDateFrom,
                      endDate: filters.hais.invasiveDeviceDateTo,
                      shortcut: filters.hais.invasiveDeviceDateShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    invasiveDeviceDateFrom: newValue?.startDate,
                    invasiveDeviceDateTo: newValue?.endDate,
                    invasiveDeviceDateShortcutName: newValue?.shortcut,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              showShortcuts
            />
            <LightSelectFilter
              config={{
                name: `origins`,
                ui: {label: 'filter.hai.origin'},
              }}
              options={sortByMessageKey(enumMap.Origin)}
              multi
              value={filters.hais.origins}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    origins: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightSelectFilter
              config={{
                name: `pathogenIds`,
                ui: {label: 'filter.hai.pathogen'},
              }}
              options={sortedAgensData ?? []}
              multi
              value={filters.hais.pathogenIds}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    pathogenIds: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightSelectFilter
              config={{
                name: `reporters`,
                ui: {label: 'filter.hai.reportedBy'},
              }}
              options={sortByMessageKey(enumMap.Reporter)}
              multi
              value={filters.hais.reporters}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    reporters: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightSelectFilter
              config={{
                name: `checkedByEpi`,
                ui: {label: 'filter.hai.checkedByEpi'},
              }}
              options={sortByMessageKey(enumMap.CheckedByEpi)}
              multi
              value={ArrayUtils.mapBooleanToEnum(filters.hais.checkedByEpi, enumMap.CheckedByEpi, (v) => tEnum('CheckedByEpi', v))}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    checkedByEpi: ArrayUtils.mapEnumToBoolean(newValue, enumMap.CheckedByEpi),
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightSelectFilter
              config={{
                name: `definitions`,
                ui: {label: 'filter.hai.definition'},
              }}
              options={sortByMessageKey(enumMap.Definition)}
              multi
              value={filters.hais.definitions}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    definitions: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightSelectFilter
              config={{
                name: `death`,
                ui: {label: 'filter.hai.death'},
              }}
              options={sortByMessageKey(enumMap.HaiDeathEnum)}
              multi
              value={filters.hais.death}
              onChange={(newValue) => {
                changeFilters({
                  hais: {
                    ...filters.hais,
                    death: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                });
              }}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightSelectFilter
              config={{
                name: 'userTags',
                ui: {label: 'hai.miniScreen.hai.userTags'},
              }}
              options={userTagOptionsWithUnassigned}
              multi
              value={getUserTagsValue()}
              onChange={handleUserTagsChange}
              maxWidth={lightSelectFilterMaxWidth}
            />
            <LightTextFilter
              config={{
                name: 'comment',
                ui: {label: 'filter.hai.comment'},
              }}
              value={filters.hais.comment}
              onChange={(newValue) =>
                changeFilters({
                  hais: {
                    ...filters.hais,
                    comment: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                })
              }
            />
            <LightTextFilter
              config={{
                name: 'patientSrcId',
                ui: {label: 'filter.hai.patientSrcId'},
              }}
              value={filters.hais.patientSrcId}
              onChange={(newValue) =>
                changeFilters({
                  hais: {
                    ...filters.hais,
                    patientSrcId: newValue,
                  },
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                })
              }
            />
          </LightFilterPanel>
        </div>
        <div data-name='hai-list-wrapper' css={muiStyles.contentBox}>
          <IconHeaderWithSearch
            iconName={'listIconMultiColor'}
            label={'hai.list.caption'}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
          >
            <LightSelectedFilters
              hasSorting={hasSorting}
              handleResetSorting={() =>
                changeFilters({
                  haisSorting: DEFAULT_HAIS_SORTING,
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                })
              }
              hasFilters={hasFilters}
              handleResetFilters={() =>
                changeFilters({
                  hais: DEFAULT_HAIS_FILTERS,
                  haisPaging: {
                    ...filters.haisPaging,
                    page: 0,
                  },
                })
              }
              hasXlsxExport
              handleXlsxExport={() => {
                if (!maxExportedRows) return;
                if (totalCount != null ? totalCount > maxExportedRows : false) {
                  setConfirmExportWarningOpened(true);
                } else {
                  downloadXlsxExport();
                }
              }}
            />
          </IconHeaderWithSearch>
          <ConfirmDialog
            isOpened={confirmExportWarningOpened}
            handleConfirm={onConfirmExportWarning}
            handleCancel={handleDialogCancel}
            showCancel={false}
            titleIcon={
              <DlMuiDynamicIcon
                iconName='haidiWarningIcon'
                useFixedViewBox='0 0 13 12'
                css={muiStyles.confirmIconButton}
                svgIconInputCss={muiStyles.confirmIcon}
                noButtonStyles
              />
            }
            titleText={t('common.export.confirmDialogLimit.title')}
            confirmText={t('common.confirmDialog.accept')}
          >
            <Typography variant='value'>{t('common.export.confirmDialogLimit.text')}</Typography>
          </ConfirmDialog>
          <MaterialTableWrapper
            options={{
              ...getDefaultMTOptions(countOnPage ?? DEFAULT_PAGE_SIZE),
            }}
            variant='big'
            useMaxHeight
            columns={columns}
            data={data ?? []}
            isLoading={isLoading}
            totalCount={totalCount}
            page={page}
            totalPages={totalPages}
            onChangePage={(page, pageSize) =>
              changeFilters({
                haisPaging: {
                  ...filters.haisPaging,
                  page,
                  pageSize,
                },
              })
            }
            onChangeRowsPerPage={(pageSize) =>
              changeFilters({
                haisPaging: {
                  ...filters.haisPaging,
                  pageSize,
                  page: clampPageNumber(filters.haisPaging.page, pageSize, totalCount),
                },
              })
            }
            controlledPageSize={filters.haisPaging.pageSize ?? DEFAULT_PAGE_SIZE}
            onOrderChange={(colIndex, direction) => {
              const columnId = columns[colIndex].field as string;
              changeFilters({
                haisSorting: {
                  field: columnId,
                  direction,
                },
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            }}
            controlledSort={(() => {
              return filters.haisSorting;
            })()}
            scrollStorageId={`${storageIdPrefix}.scrollXY`}
          />
        </div>
      </LightSplitLayout>
    </div>
  );
};

HaiListLayout.displayName = 'HaiListLayout';

export default HaiListLayout;

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  splitContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
});
